import { DataGridPremium, GridActionsCellItem, GridAlignment, GridColumnMenu, GridColumnMenuProps } from '@mui/x-data-grid-premium'
import { Grid, Tooltip, Typography } from '@mui/material'
import { Check, Close } from '@mui/icons-material'
import { useCallback, useEffect, useState } from 'react'
import ConfirmationDialog from '../../components/ConfirmationDialog'
import { UserRequest } from '../../libs/api/users/UserModel'
import UserRequestConfirmationDialog from './UserRequestConfirmationDialog'
import { Organization } from '../../libs/api/organization/OrganizationModel'

interface InviteResponseAcceptance {
    userId: string
    userEmail: string
    isApproved: boolean
}

export interface UserRequestsGridProps {
    userRequests: UserRequest[]
    organizations: Organization[]
    loading: boolean
    onInviteResponse: (userId: string, hasApproved?: boolean, organization?: string, rejectReason?: string) => void
}

export function UserRequestsGrid(props: UserRequestsGridProps) {
    const { userRequests, organizations, loading, onInviteResponse } = props
    const [responseInvite, setResponseInvite] = useState<InviteResponseAcceptance>()
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [approveDialogOpen, setApproveDialogOpen] = useState<boolean>(false)

    const handleInviteResponseAction = (userId: string, userEmail: string, isApproved: boolean) => {
        setResponseInvite({ userId, userEmail, isApproved })
        setApproveDialogOpen(true)
    }

    const handleInviteResponse = useCallback(
        async (organization?: string, rejectReason?: string) => {
            onInviteResponse(responseInvite.userId, responseInvite.isApproved, organization, rejectReason)
            setResponseInvite(null)
        },
        [responseInvite, onInviteResponse]
    )

    const getActions = getActionsArray(handleInviteResponseAction)
    const builtColumns = buildColumns(getActions)

    function CustomColumnMenu(props: GridColumnMenuProps) {
        return (
            <GridColumnMenu
                {...props}
                slots={{
                    columnMenuGroupingItem: null,
                    columnMenuPinningItem: null,
                    columnMenuColumnsItem: null,
                }}
            />
        )
    }

    return (
        <>
            <DataGridPremium
                getRowId={(r) => r.id}
                pagination
                autoHeight
                loading={loading}
                pageSizeOptions={[25, 50, 100, 250]}
                sortingOrder={['asc', 'desc']}
                rows={userRequests || []}
                columns={builtColumns}
                initialState={initialState}
                slots={{
                    noRowsOverlay: () => <NoUserRequestsOverlay />,
                    columnMenu: CustomColumnMenu,
                }}
                disableRowSelectionOnClick
                hideFooter
                sx={{
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '.MuiDataGrid-columnHeaders': {
                        backgroundImage: 'linear-gradient(rgba(240, 240, 255, 0.35), rgba(240, 240, 255, 0.1))',
                        borderBottom: '2px solid rgba(27, 73, 212, 1)',
                    },
                    '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                    },
                    '.MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    border: '2px solid rgba(34, 34, 58, 0.3)',
                    marginTop: '15px',
                    marginBottom: '20px',
                    width: '100%',
                }}
            />
            <ConfirmationDialog
                openDialog={dialogOpen}
                title={'Are you sure?'}
                description={`${responseInvite?.userEmail} is waiting for approval. Declining the request means they will not have access to M-MAP portal.`}
                confirmText={'Decline'}
                cancelText={'Cancel'}
                onConfirm={() => {
                    setDialogOpen(false)
                    handleInviteResponse()
                    setResponseInvite(null)
                }}
                onCancel={() => {
                    setDialogOpen(false)
                    setResponseInvite(null)
                }}
            />
            <UserRequestConfirmationDialog
                openDialog={approveDialogOpen}
                organizations={organizations}
                userName={responseInvite?.userEmail}
                isApproved={responseInvite?.isApproved}
                onApprove={(organization?: string) => {
                    setApproveDialogOpen(false)
                    handleInviteResponse(organization)
                }}
                onReject={(rejectReason?: string) => {
                    setApproveDialogOpen(false)
                    handleInviteResponse(null, rejectReason)
                }}
                onCancel={() => {
                    setApproveDialogOpen(false)
                    setResponseInvite(null)
                }}
            />
        </>
    )
}

/**
 * Grid columns setup
 * @param getActionsArray Callback to obtain the array of actions for a samples elements
 * @returns The rendering component array for the Grid
 */
const buildColumns = (getActionsArray: (params: any) => React.JSX.Element[]) => {
    return [
        { field: 'createdDate', flex: 1, headerName: 'Request Date' },
        { field: 'name', flex: 1, headerName: 'Name' },
        { field: 'email', flex: 2, headerName: 'Email' },
        { field: 'enteredOrganization', headerName: 'Entered Organization', flex: 2 },
        { field: 'actions', type: 'actions', flex: 1, minWidth: 170, align: 'right' as GridAlignment, getActions: getActionsArray },
    ]
}

/**
 * Builds an array of action components for a invite response action
 * @param onInviteResponse Callback to respond to an invite
 * @returns The array of action components for the project invites
 */
function getActionsArray(onInviteResponse?: (userId: string, userEmail: string, hasAccepted: boolean) => void) {
    const actionsArray = (params: any) => {
        if (params.id.startsWith('auto-generated-row-invites')) {
            return []
        }

        return [
            <GridActionsCellItem
                key="decline"
                icon={
                    <Tooltip title="Decline user request">
                        <Grid sx={{ display: 'flex', flexDirection: 'row', color: 'rgba(211, 47, 47, 1)' }}>
                            <Close fontSize="small" />
                            <Typography variant="body2">Decline</Typography>
                        </Grid>
                    </Tooltip>
                }
                onClick={() => {
                    onInviteResponse(params.id, params.row.email, false)
                }}
                label="Decline"
            />,
            <GridActionsCellItem
                key="accept"
                icon={
                    <Tooltip title="Accept user request">
                        <Grid sx={{ display: 'flex', flexDirection: 'row', color: 'rgba(23, 98, 39, 1)' }}>
                            <Check fontSize="small" />
                            <Typography variant="body2">Accept</Typography>
                        </Grid>
                    </Tooltip>
                }
                onClick={() => {
                    onInviteResponse(params.id, params.row.email, true)
                }}
                label="Accept"
            />,
        ]
    }

    return actionsArray
}

/**
 * The datagrid initial state
 */
const initialState = {
    pagination: {
        paginationModel: { pageSize: 30, page: 0 },
    },
    rowGrouping: {
        model: ['invites'],
    },
    columns: {
        columnVisibilityModel: {
            invites: false,
            projectId: false,
        },
    },
}

const NoUserRequestsOverlay = () => {
    const [show, setShow] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 300)
        return () => clearTimeout(timeout)
    }, [])

    if (!show) return null
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
            }}
        >
            <p>No pending user requests found</p>
        </div>
    )
}
